import React, { useState, useRef } from 'react';
import { Upload, ChevronDown } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './CareersPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareersPage = () => {
  const formRef = useRef(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formData, setFormData] = useState({
    nomeCompleto: '',
    email: '',
    telefone: '',
    posicao: '',
    anosExperiencia: '',
    disponibilidade: '',
    mensagem: ''
  });
  const [cvFile, setCvFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const positions = [
    {
      id: 'psicologo',
      title: 'Psicólogo(a)',
      type: 'Tempo integral',
      location: 'Lisboa',
      description: 'Acompanhamento psicológico e intervenção clínica'
    },
    {
      id: 'tecnico-servico-social',
      title: 'Técnico(a) de Serviço Social',
      type: 'Tempo integral',
      location: 'Lisboa',
      description: 'Apoio social e acompanhamento de casos'
    },
    {
      id: 'tecnico-psicossocial',
      title: 'Técnico Psicossocial',
      type: 'Tempo integral',
      location: 'Lisboa',
      description: 'Intervenção psicossocial comunitária'
    },
    {
      id: 'designer',
      title: 'Designer',
      type: 'Freelance',
      location: 'Lisboa',
      description: 'Desenvolvimento de materiais de comunicação para projetos na área da saúde mental e intervenção social.'
    },
    {
      id: 'social-media-manager',
      title: 'Social Media Manager',
      type: 'Part-time/Freelance',
      location: 'Remoto',
      description: 'Gestão de redes sociais e comunicação digital'
    },
    {
      id: 'freelance-photographer',
      title: 'Freelance Photographer',
      type: 'Freelance',
      location: 'Lisboa/Remoto',
      description: 'Reportagem fotográfica de eventos e atividades'
    },
    {
      id: 'voluntario',
      title: 'Voluntário(a)',
      type: 'Voluntariado',
      location: 'Lisboa',
      description: 'Apoio em atividades e projetos comunitários'
    },
    {
      id: 'candidatura-espontanea',
      title: 'Candidatura Espontânea',
      type: 'Várias modalidades',
      location: 'Portugal',
      description: 'Envie o seu perfil'
    }
  ];

  const experienceOptions = [
    'Sem experiência',
    '< 1 ano',
    '1-2 anos',
    '3-5 anos',
    '5-10 anos',
    '> 10 anos'
  ];

  const availabilityOptions = [
    'Imediata',
    '15 dias',
    '1 mês',
    '2 meses',
    'A combinar'
  ];

  const handlePositionClick = (position) => {
    setSelectedPosition(position);
    setFormData(prev => ({ ...prev, posicao: position.title }));
    
    // Scroll to form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      setError('Por favor, selecione um ficheiro PDF, DOC ou DOCX.');
      return;
    }

    if (file.size > maxSize) {
      setError('O ficheiro não pode exceder 10MB.');
      return;
    }

    setCvFile(file);
    setError(null);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nome_completo', formData.nomeCompleto);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telefone', formData.telefone);
      formDataToSend.append('posicao', formData.posicao);
      formDataToSend.append('anos_experiencia', formData.anosExperiencia);
      formDataToSend.append('disponibilidade', formData.disponibilidade);
      formDataToSend.append('mensagem', formData.mensagem);
      
      if (cvFile) {
        formDataToSend.append('cv', cvFile);
      }

      await axios.post(`${API}/public/candidatura`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitted(true);
      setFormData({
        nomeCompleto: '',
        email: '',
        telefone: '',
        posicao: '',
        anosExperiencia: '',
        disponibilidade: '',
        mensagem: ''
      });
      setCvFile(null);
      setSelectedPosition(null);

      setTimeout(() => {
        setSubmitted(false);
      }, 8000);
    } catch (err) {
      console.error('Erro ao enviar candidatura:', err);
      setError('Ocorreu um erro ao enviar a candidatura. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="careers-page" data-testid="careers-page">
      <SEO 
        title="Carreiras"
        description="Junte-se à equipa da AASPS. Consulte as oportunidades de emprego e envie a sua candidatura espontânea."
        keywords="carreiras, emprego, vagas, trabalhar, AASPS, candidatura"
        url="/carreiras"
      />
      <Header />

      {/* Hero Section */}
      <section className="careers-hero">
        <div className="careers-hero-overlay" />
        <div className="careers-hero-content">
          <h1>CARREIRA</h1>
          <p>Junte-se à nossa equipa e faça parte de uma associação em crescimento</p>
        </div>
      </section>

      {/* Positions Grid */}
      <section className="careers-positions" data-testid="positions-section">
        <div className="careers-container">
          <div className="positions-grid">
            {positions.map((position) => (
              <div 
                key={position.id}
                className={`position-card ${selectedPosition?.id === position.id ? 'selected' : ''}`}
                onClick={() => handlePositionClick(position)}
                data-testid={`position-${position.id}`}
              >
                <h3>{position.title}</h3>
                <p className="position-type">{position.type} • {position.location}</p>
                <p className="position-description">{position.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section ref={formRef} className="careers-form-section" data-testid="application-form-section">
        <div className="careers-container">
          {submitted ? (
            <div className="form-success" data-testid="form-success">
              <h3>Candidatura Enviada!</h3>
              <p>Obrigado pelo seu interesse em fazer parte da AASPS. Entraremos em contacto brevemente.</p>
            </div>
          ) : (
            <form className="careers-form" onSubmit={handleSubmit} data-testid="careers-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nomeCompleto">Nome Completo *</label>
                  <input
                    type="text"
                    id="nomeCompleto"
                    name="nomeCompleto"
                    placeholder="Nome completo"
                    value={formData.nomeCompleto}
                    onChange={handleInputChange}
                    required
                    data-testid="input-nome"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="exemplo@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telefone">Telefone *</label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    placeholder="+351 XXX XXX XXX"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    required
                    data-testid="input-telefone"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="posicao">Posição Desejada *</label>
                  <div className="select-wrapper">
                    <select
                      id="posicao"
                      name="posicao"
                      value={formData.posicao}
                      onChange={handleInputChange}
                      required
                      data-testid="select-posicao"
                    >
                      <option value="">Posição Desejada</option>
                      {positions.map((pos) => (
                        <option key={pos.id} value={pos.title}>{pos.title}</option>
                      ))}
                    </select>
                    <ChevronDown className="select-icon" />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="anosExperiencia">Anos de Experiência</label>
                  <div className="select-wrapper">
                    <select
                      id="anosExperiencia"
                      name="anosExperiencia"
                      value={formData.anosExperiencia}
                      onChange={handleInputChange}
                      data-testid="select-experiencia"
                    >
                      <option value="">Selecionar experiência</option>
                      {experienceOptions.map((exp) => (
                        <option key={exp} value={exp}>{exp}</option>
                      ))}
                    </select>
                    <ChevronDown className="select-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="disponibilidade">Disponibilidade</label>
                  <div className="select-wrapper">
                    <select
                      id="disponibilidade"
                      name="disponibilidade"
                      value={formData.disponibilidade}
                      onChange={handleInputChange}
                      data-testid="select-disponibilidade"
                    >
                      <option value="">Selecionar disponibilidade</option>
                      {availabilityOptions.map((avail) => (
                        <option key={avail} value={avail}>{avail}</option>
                      ))}
                    </select>
                    <ChevronDown className="select-icon" />
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Curriculum Vitae *</label>
                <div 
                  className={`cv-upload-area ${dragActive ? 'drag-active' : ''} ${cvFile ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('cv-input').click()}
                  data-testid="cv-upload"
                >
                  <input
                    type="file"
                    id="cv-input"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                  {cvFile ? (
                    <div className="cv-file-info">
                      <span className="cv-file-name">{cvFile.name}</span>
                      <span className="cv-file-size">({(cvFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="upload-icon" />
                      <p className="upload-text">Arraste ou clique para carregar</p>
                      <p className="upload-hint">PDF, DOC ou DOCX até 10MB</p>
                    </>
                  )}
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="mensagem">Mensagem / Motivação</label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  placeholder="Fale-nos sobre si e porque gostaria de fazer parte da equipa AASPS..."
                  rows="5"
                  value={formData.mensagem}
                  onChange={handleInputChange}
                  data-testid="textarea-mensagem"
                />
              </div>

              {error && (
                <div className="form-error" data-testid="form-error">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={submitting || !cvFile}
                data-testid="submit-btn"
              >
                {submitting ? 'A enviar...' : 'ENVIAR CANDIDATURA'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
